<script setup lang="ts">
import { onMounted, onUnmounted, computed, nextTick, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useGlobalSearch, type SearchEntityType } from '@/composables/useGlobalSearch'
import { useProjectsStore } from '@/views/Projects/store'
import { Dialog } from '@/components/ui/dialog'
import { Command, CommandList, CommandGroup, CommandItem, CommandSeparator } from '@/components/ui/command'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  DialogClose,
  DialogContent,
  DialogOverlay,
  DialogPortal,
} from 'reka-ui'
import {
  Search,
  X,
  SlidersHorizontal,
  Clock,
  ArrowRight,
  Loader2,
  FolderKanban,
  AppWindow,
  FileText,
  TicketCheck,
  CalendarDays,
  Building2,
} from 'lucide-vue-next'

const router = useRouter()
const inputRef = ref<HTMLInputElement>()

const {
  isOpen,
  searchQuery,
  debouncedQuery,
  isAdvancedMode,
  advancedFilters,
  recentSearches,
  isLoadingStores,
  searchGroups,
  totalResults,
  navigateToResult,
  selectRecentSearch,
  removeRecentSearch,
  clearRecentSearches,
  toggleEntityType,
  resetAdvancedFilters,
} = useGlobalSearch()

const ENTITY_TYPE_OPTIONS: { type: SearchEntityType; label: string; icon: typeof FolderKanban }[] = [
  { type: 'project', label: 'Proyectos', icon: FolderKanban },
  { type: 'application', label: 'Apps', icon: AppWindow },
  { type: 'requirement', label: 'Requerimientos', icon: FileText },
  { type: 'ticket', label: 'Tickets', icon: TicketCheck },
  { type: 'meeting', label: 'Reuniones', icon: CalendarDays },
  { type: 'organization', label: 'Organizaciones', icon: Building2 },
]

const projectsStore = useProjectsStore()

const projectOptions = computed(() =>
  projectsStore.projects.map((p) => ({ value: p.id, label: p.name })),
)

const showRecentSearches = computed(
  () => !debouncedQuery.value && recentSearches.value.length > 0,
)

const showEmpty = computed(
  () => debouncedQuery.value && searchGroups.value.length === 0 && !isLoadingStores.value,
)

const showIdleState = computed(
  () => !debouncedQuery.value && recentSearches.value.length === 0 && !isLoadingStores.value,
)

function isEntityTypeActive(type: SearchEntityType): boolean {
  const types = advancedFilters.value.entityTypes
  return types.length === 0 || types.includes(type)
}

function handleNavigateToList(routeName: string) {
  isOpen.value = false
  router.push({ name: routeName })
}

// Cmd+K / Ctrl+K global shortcut
function handleKeydown(e: KeyboardEvent) {
  if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
    e.preventDefault()
    isOpen.value = !isOpen.value
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
})

// Focus input when dialog opens
function handleOpenChange(open: boolean) {
  isOpen.value = open
  if (open) {
    nextTick(() => {
      inputRef.value?.focus()
    })
  }
}
</script>

<template>
  <!-- Trigger Button -->
  <Button
    variant="outline"
    class="relative h-9 w-9 px-0 md:h-9 md:w-auto md:px-4 md:pr-12"
    @click="isOpen = true"
  >
    <Search class="h-4 w-4 md:mr-2" />
    <span class="hidden md:inline-flex text-sm text-muted-foreground">Buscar...</span>
    <kbd
      class="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 hidden md:inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground"
    >
      <span class="text-xs">&#8984;</span>K
    </kbd>
  </Button>

  <!-- Custom Dialog (not CommandDialog, to control width and avoid close button overlap) -->
  <Dialog :open="isOpen" @update:open="handleOpenChange">
    <DialogPortal>
      <DialogOverlay
        class="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
      />
      <DialogContent
        class="fixed left-1/2 top-1/2 z-50 w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-lg border bg-background p-0 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]"
        @escape-key-down="isOpen = false"
      >
        <DialogClose class="sr-only">Cerrar</DialogClose>
        <Command
          class="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5"
        >
          <!-- Custom Input (not CommandInput to avoid internal filtering) -->
          <div class="flex items-center border-b px-3">
            <Search class="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <input
              ref="inputRef"
              v-model="searchQuery"
              class="flex h-11 w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
              placeholder="Buscar proyectos, requerimientos, tickets..."
            />
            <div class="flex items-center gap-1 shrink-0">
              <Button
                v-if="searchQuery"
                variant="ghost"
                size="icon"
                class="h-7 w-7"
                @click="searchQuery = ''"
              >
                <X class="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                class="h-7 w-7"
                :class="{ 'bg-accent': isAdvancedMode }"
                @click="isAdvancedMode = !isAdvancedMode"
              >
                <SlidersHorizontal class="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>

          <!-- Advanced Filters Panel -->
          <div v-if="isAdvancedMode" class="border-b p-3 space-y-3">
            <!-- Entity Type Chips -->
            <div class="flex flex-wrap gap-1.5">
              <button
                v-for="opt in ENTITY_TYPE_OPTIONS"
                :key="opt.type"
                class="inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors"
                :class="
                  isEntityTypeActive(opt.type)
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-transparent text-muted-foreground border-input hover:bg-accent'
                "
                @click="toggleEntityType(opt.type)"
              >
                <component :is="opt.icon" class="h-3 w-3" />
                {{ opt.label }}
              </button>
            </div>

            <!-- Project Filter + Date Range -->
            <div class="flex flex-wrap gap-2">
              <Select
                :model-value="advancedFilters.projectId || '_all'"
                @update:model-value="advancedFilters.projectId = $event === '_all' ? '' : $event"
              >
                <SelectTrigger class="h-8 w-[180px] text-xs">
                  <SelectValue placeholder="Proyecto..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">Todos los proyectos</SelectItem>
                  <SelectItem v-for="p in projectOptions" :key="p.value" :value="p.value">
                    {{ p.label }}
                  </SelectItem>
                </SelectContent>
              </Select>

              <input
                v-model="advancedFilters.dateFrom"
                type="date"
                class="h-8 rounded-md border border-input bg-transparent px-2 text-xs"
                placeholder="Desde"
              />
              <input
                v-model="advancedFilters.dateTo"
                type="date"
                class="h-8 rounded-md border border-input bg-transparent px-2 text-xs"
                placeholder="Hasta"
              />

              <Button variant="ghost" size="sm" class="h-8 text-xs" @click="resetAdvancedFilters">
                Limpiar
              </Button>
            </div>
          </div>

          <CommandList class="max-h-[400px]">
            <!-- Loading State -->
            <div v-if="isLoadingStores" class="py-6 text-center text-sm text-muted-foreground">
              <Loader2 class="mx-auto h-5 w-5 animate-spin mb-2" />
              Cargando datos...
            </div>

            <!-- Idle State (no query, no recent searches) -->
            <template v-else-if="showIdleState">
              <div class="px-4 pt-4 pb-2">
                <p class="text-xs font-medium text-muted-foreground uppercase tracking-wider">Accesos rapidos</p>
              </div>
              <div class="px-2 pb-2 grid grid-cols-3 gap-1.5">
                <button
                  v-for="opt in ENTITY_TYPE_OPTIONS"
                  :key="opt.type"
                  class="flex flex-col items-center gap-1.5 rounded-lg border border-transparent px-3 py-3 text-muted-foreground hover:bg-muted hover:text-foreground hover:border-border transition-colors"
                  @click="toggleEntityType(opt.type); isAdvancedMode = true"
                >
                  <component :is="opt.icon" class="h-5 w-5" />
                  <span class="text-xs font-medium">{{ opt.label }}</span>
                </button>
              </div>
              <div class="px-4 pt-2 pb-3 border-t">
                <p class="text-xs text-muted-foreground">
                  Escribe para buscar en proyectos, requerimientos, tickets y mas.
                </p>
                <div class="flex items-center gap-3 mt-2 text-[10px] text-muted-foreground/60">
                  <span><kbd class="rounded border px-1 py-0.5 font-mono">&#8984;K</kbd> Abrir busqueda</span>
                  <span><kbd class="rounded border px-1 py-0.5 font-mono">esc</kbd> Cerrar</span>
                </div>
              </div>
            </template>

            <!-- Recent Searches (when input is empty) -->
            <template v-else-if="showRecentSearches">
              <CommandGroup heading="Busquedas recientes">
                <CommandItem
                  v-for="term in recentSearches"
                  :key="term"
                  :value="term"
                  class="group"
                  @select="selectRecentSearch(term)"
                >
                  <Clock class="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span class="flex-1 truncate">{{ term }}</span>
                  <button
                    class="ml-auto h-5 w-5 shrink-0 inline-flex items-center justify-center rounded-sm opacity-0 group-hover:opacity-100 hover:bg-accent"
                    @click.stop="removeRecentSearch(term)"
                  >
                    <X class="h-3 w-3" />
                  </button>
                </CommandItem>
              </CommandGroup>
              <div class="flex justify-end px-3 py-1.5">
                <Button variant="link" size="sm" class="h-auto p-0 text-xs" @click="clearRecentSearches">
                  Limpiar historial
                </Button>
              </div>
            </template>

            <!-- Search Results -->
            <template v-else-if="searchGroups.length > 0">
              <template v-for="(group, groupIdx) in searchGroups" :key="group.entityType">
                <CommandSeparator v-if="groupIdx > 0" />
                <CommandGroup :heading="group.label">
                  <CommandItem
                    v-for="result in group.results"
                    :key="result.id"
                    :value="`${result.entityType}-${result.id}`"
                    @select="navigateToResult(result)"
                  >
                    <component :is="group.icon" class="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span class="flex-1 truncate">{{ result.title }}</span>
                    <span v-if="result.subtitle" class="shrink-0 text-xs text-muted-foreground">
                      {{ result.subtitle }}
                    </span>
                    <Badge v-if="result.badge" :variant="result.badgeVariant" class="shrink-0 text-[10px]">
                      {{ result.badge }}
                    </Badge>
                  </CommandItem>

                  <!-- "Ver todos" link -->
                  <CommandItem
                    v-if="group.totalCount > group.results.length"
                    :value="`see-all-${group.entityType}`"
                    class="text-xs text-muted-foreground"
                    @select="handleNavigateToList(group.listRouteName)"
                  >
                    <ArrowRight class="h-3 w-3" />
                    <span>Ver todos ({{ group.totalCount }})</span>
                  </CommandItem>
                </CommandGroup>
              </template>
            </template>

            <!-- Empty State -->
            <div v-else-if="showEmpty" class="py-6 text-center text-sm text-muted-foreground">
              No se encontraron resultados para "{{ debouncedQuery }}"
            </div>
          </CommandList>

          <!-- Footer -->
          <div
            v-if="debouncedQuery || showRecentSearches"
            class="flex items-center justify-between border-t px-3 py-2 text-xs text-muted-foreground"
          >

            <div class="flex items-center gap-3">
              <span><kbd class="rounded border px-1 py-0.5 font-mono text-[10px]">&uarr;&darr;</kbd> Navegar</span>
              <span><kbd class="rounded border px-1 py-0.5 font-mono text-[10px]">&crarr;</kbd> Seleccionar</span>
              <span><kbd class="rounded border px-1 py-0.5 font-mono text-[10px]">esc</kbd> Cerrar</span>
            </div>
            <span v-if="debouncedQuery">{{ totalResults }} resultado{{ totalResults !== 1 ? 's' : '' }}</span>
          </div>
        </Command>
      </DialogContent>
    </DialogPortal>
  </Dialog>
</template>
